import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSocket } from '../contexts/SocketContext';
import { FaClipboardList, FaGift, FaDollarSign, FaTrophy, FaChartLine, FaCalendarAlt, FaStar, FaArrowRight, FaBolt, FaLeaf, FaPlay } from 'react-icons/fa';
import axios from 'axios';
import styled, { keyframes, css } from 'styled-components';
import LoadingSpinner from '../components/LoadingSpinner';
import { toast } from 'react-toastify';

// Brand Palette
const colors = {
  primary: '#15803d',
  sysGreen: '#16a34a',
  darkGreen: '#14532d',
  navy: '#0f172a',
  lightBg: '#f0fdf4',
  white: '#ffffff',
  textMain: '#1e293b',
  textSub: '#64748b',
  accent: '#84cc16',
};

// --- Animations ---

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
`;

const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0px); }
`;

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(22, 163, 74, 0.4); }
  70% { box-shadow: 0 0 0 15px rgba(22, 163, 74, 0); }
  100% { box-shadow: 0 0 0 0 rgba(22, 163, 74, 0); }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

// --- Styled Components ---

const DashboardContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 1.5rem 1.5rem 2rem;
  font-family: 'Inter', sans-serif;
  background-color: ${colors.lightBg};

  @media (max-width: 768px) {
    padding: 1rem 1rem 2rem;
  }
`;

const WelcomeSection = styled.div`
  background: linear-gradient(90deg, ${colors.sysGreen} 0%, ${colors.darkGreen} 100%);
  color: white;
  padding: 1.5rem 2.5rem;
  border-radius: 16px;
  width: 100%;
  margin-bottom: 2rem;
  box-shadow: 0 10px 25px -5px rgba(22, 163, 74, 0.4);
  position: relative;
  overflow: hidden;
  animation: ${fadeInUp} 0.8s cubic-bezier(0.2, 0.8, 0.2, 1);
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  min-height: 100px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1.5rem;
    padding: 1.5rem;
  }

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 50%);
    pointer-events: none;
  }

  .content {
    position: relative;
    z-index: 10;
  }

  h1 {
    margin: 0 0 0.25rem 0;
    font-size: 1.75rem;
    font-weight: 800;
    letter-spacing: -0.5px;
    line-height: 1.2;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  p {
    margin: 0;
    opacity: 0.9;
    font-size: 0.95rem;
    font-weight: 500;
  }

  .actions {
    position: relative;
    z-index: 10;
    margin: 0;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1.25rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div`
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(12px);
  padding: 1.25rem;
  border-radius: 20px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
  display: flex;
  align-items: center;
  gap: 1rem;
  transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  border: 1px solid rgba(255, 255, 255, 0.6);
  position: relative;
  overflow: hidden;
  animation: ${fadeInUp} 0.6s cubic-bezier(0.2, 0.8, 0.2, 1) backwards;

  &:nth-child(1) { animation-delay: 0.1s; }
  &:nth-child(2) { animation-delay: 0.2s; }
  &:nth-child(3) { animation-delay: 0.3s; }
  &:nth-child(4) { animation-delay: 0.4s; }

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
    background: white;
    border-color: rgba(22, 163, 74, 0.2);

    .icon-bg {
      transform: scale(1.1) rotate(5deg);
    }
  }

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 3px;
    background: ${props => props.accent || colors.sysGreen};
    opacity: 0;
    transform: scaleX(0);
    transform-origin: left;
    transition: all 0.4s ease;
  }

  &:hover::after {
    opacity: 1;
    transform: scaleX(1);
  }
`;

const StatIcon = styled.div`
  font-size: 1.4rem;
  width: 48px;
  height: 48px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.bg || '#dcfce7'};
  color: ${props => props.color || colors.sysGreen};
  transition: transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
`;

const StatContent = styled.div`
  h3 {
    margin: 0;
    font-size: 1.4rem;
    font-weight: 800;
    color: ${colors.navy};
    line-height: 1.1;
    letter-spacing: -0.5px;
  }

  p {
    margin: 0;
    color: ${colors.textSub};
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-top: 0.2rem;
  }
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1.1fr;
  grid-template-rows: auto auto;
  gap: 0.75rem;
  animation: ${fadeInUp} 0.8s cubic-bezier(0.2, 0.8, 0.2, 1) 0.4s backwards;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: ${colors.white};
  border-radius: 16px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(226, 232, 240, 0.8);
  display: flex;
  flex-direction: column;
  transition: transform 0.4s ease, box-shadow 0.4s ease;
  overflow: hidden;

  &:hover {
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.06);
    transform: translateY(-2px);
  }
`;

const CardHeader = styled.div`
  padding: 0.7rem 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #f1f5f9;
  background: rgba(248, 250, 252, 0.5);

  h2 {
    margin: 0;
    color: ${colors.navy};
    font-size: 0.95rem;
    font-weight: 700;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .icon-wrapper {
    width: 24px;
    height: 24px;
    background: white;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.75rem;
    color: ${colors.navy};
    box-shadow: 0 1px 2px rgba(0,0,0,0.05);
  }
`;

const CardContent = styled.div`
  padding: 0.6rem 1rem;
  flex: 1;
`;

const SurveyItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.6rem 0.85rem;
  margin-bottom: 0.4rem;
  background: #fff;
  border: 1px solid #f1f5f9;
  border-radius: 12px;
  cursor: ${props => props.$clickable ? 'pointer' : 'default'};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;

  &:last-child {
    margin-bottom: 0;
  }

  &:hover {
    background: #ffffff;
    border-color: ${props => props.$clickable ? colors.sysGreen : '#f1f5f9'};
    transform: ${props => props.$clickable ? 'translateY(-2px)' : 'none'};
    box-shadow: ${props => props.$clickable ? '0 8px 16px -6px rgba(0,0,0,0.06)' : 'none'};
  }

  ${props => props.$new && css`
    &::after {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: linear-gradient(to right, transparent 0%, rgba(255,255,255,0.8) 50%, transparent 100%);
      background-size: 200% 100%;
      animation: ${shimmer} 2.5s infinite linear;
      opacity: 0.15;
      pointer-events: none;
    }
  `}
`;

const SurveyInfo = styled.div`
  h4 {
    margin: 0 0 0.1rem 0;
    color: ${colors.textMain};
    font-size: 0.82rem;
    font-weight: 700;
  }

  p {
    margin: 0;
    color: ${colors.textSub};
    font-size: 0.75rem;
    display: flex;
    align-items: center;
    gap: 0.4rem;
    font-weight: 500;
  }
`;

const PointsBadge = styled.span`
  background: ${props => props.bg || '#dcfce7'};
  color: ${props => props.color || colors.sysGreen};
  padding: 0.25rem 0.6rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  transition: all 0.3s ease;

  ${SurveyItem}:hover & {
    transform: scale(1.05);
    background: ${colors.sysGreen};
    color: white;
  }
`;

const ActionButton = styled(Link)`
  background: ${colors.white};
  color: ${colors.sysGreen};
  padding: 0.75rem 1.5rem;
  border-radius: 30px;
  text-decoration: none;
  font-weight: 700;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 12px -1px rgba(0, 0, 0, 0.15);
    background: #f8fafc;
  }
`;

const RedeemButton = styled.button`
  background: linear-gradient(135deg, ${colors.navy} 0%, #1e293b 100%);
  color: white;
  width: 100%;
  padding: 0.6rem;
  border-radius: 10px;
  border: none;
  font-weight: 700;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 0.5rem;
  box-shadow: 0 6px 10px -2px rgba(15, 23, 42, 0.2);
  position: relative;
  overflow: hidden;

  ${props => !props.disabled && css`
    animation: ${pulse} 2.5s infinite;
  `}

  &:hover:not(:disabled) {
    transform: translateY(-2px) scale(1.01);
    box-shadow: 0 10px 15px -3px rgba(15, 23, 42, 0.3);
  }

  &:disabled {
    background: #e2e8f0;
    color: #94a3b8;
    cursor: not-allowed;
    box-shadow: none;
    animation: none;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 1.5rem 1rem;
  background: #f8fafc;
  border-radius: 12px;
  border: 2px dashed #e2e8f0;
  transition: all 0.3s ease;

  &:hover {
    border-color: ${colors.sysGreen};
    background: #f0fdf4;
  }

  svg {
    color: ${colors.textSub};
    opacity: 0.3;
    font-size: 2rem;
    margin-bottom: 0.5rem;
    animation: ${float} 4s ease-in-out infinite;
  }

  p {
    color: ${colors.textSub};
    font-size: 0.82rem;
    font-weight: 500;
  }
`;

const QuickActionItem = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.6rem 0.85rem;
  background: #ffffff;
  border: 1px solid #f1f5f9;
  border-radius: 10px;
  text-decoration: none;
  color: ${colors.textMain};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  margin-bottom: 0.4rem;
  box-shadow: 0 2px 4px rgba(0,0,0,0.02);

  &:last-child {
    margin-bottom: 0;
  }

  &:hover {
    background: #ffffff;
    border-color: ${colors.sysGreen};
    transform: translateX(6px);
    box-shadow: 0 8px 12px -3px rgba(0, 0, 0, 0.05);

    .icon-box {
      background: ${colors.sysGreen};
      color: white;
      transform: scale(1.1) rotate(10deg);
      box-shadow: 0 4px 8px -2px rgba(22, 163, 74, 0.3);
    }

    .arrow-icon {
      opacity: 1;
      transform: translateX(0);
    }
  }

  .icon-box {
    width: 34px;
    height: 34px;
    border-radius: 8px;
    background: #f1f5f9;
    color: ${colors.textSub};
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1rem;
    transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  }

  .text-box {
    flex: 1;
    h4 {
      margin: 0 0 0.1rem 0;
      font-size: 0.82rem;
      font-weight: 700;
      color: ${colors.navy};
    }
    span {
      display: block;
      font-size: 0.7rem;
      color: ${colors.textSub};
    }
  }

  .arrow-icon {
    opacity: 0;
    transform: translateX(-10px);
    transition: all 0.3s ease;
    color: ${colors.sysGreen};
    font-size: 0.9rem;
  }
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background: #e2e8f0;
  border-radius: 6px;
  overflow: hidden;
  position: relative;
  box-shadow: inset 0 2px 4px rgba(0,0,0,0.05);

  .fill {
    height: 100%;
    background: linear-gradient(90deg, ${colors.sysGreen}, ${colors.accent});
    border-radius: 6px;
    transition: width 1.5s cubic-bezier(0.34, 1.56, 0.64, 1);
    width: 0%;
    position: relative;
    box-shadow: 0 2px 4px rgba(22, 163, 74, 0.3);

    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      bottom: 0;
      right: 0;
      background: linear-gradient(
        90deg,
        rgba(255,255,255,0) 0%,
        rgba(255,255,255,0.4) 50%,
        rgba(255,255,255,0) 100%
      );
      background-size: 30px 30px;
      animation: ${shimmer} 2s infinite linear;
    }
  }
`;

// --- Component ---

const Dashboard = () => {
  const { user, refreshUser } = useAuth();
  const { socket } = useSocket();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [animatedProgress, setAnimatedProgress] = useState(0);

  const handleSurveyClick = (surveyId) => {
    navigate(`/surveys/${surveyId}`);
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/users/dashboard');
      if (response.data.success) {
        setDashboardData(response.data.dashboard);
      } else {
        setError('Failed to load dashboard data');
      }
    } catch (error) {
      console.error('Dashboard fetch error:', error);
      setError('Failed to load dashboard data. Error: ' + (error.response?.data?.message || error.message));
    } finally {
      setTimeout(() => setLoading(false), 500);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, [user?.points, user]);

  useEffect(() => {
    if (dashboardData?.stats?.currentPoints) {
      const timer = setTimeout(() => {
        const percentage = Math.min(100, (dashboardData.stats.currentPoints / 500) * 100);
        setAnimatedProgress(percentage);
      }, 600);
      return () => clearTimeout(timer);
    }
  }, [dashboardData]);

  useEffect(() => {
    if (socket && user) {
      const handlePointsUpdate = async (data) => {
        if (data.reason === 'withdrawal_approved') {
          toast.success(data.message);
        } else if (data.reason === 'withdrawal_rejected') {
          toast.info(data.message);
        }
        await refreshUser();
        await fetchDashboardData();
      };
      socket.on('pointsUpdated', handlePointsUpdate);
      return () => {
        socket.off('pointsUpdated', handlePointsUpdate);
      };
    }
  }, [socket, user, refreshUser, fetchDashboardData]);

  useEffect(() => {
    const completed = searchParams.get('completed');
    const refresh = searchParams.get('refresh');
    if (completed === 'true' && refresh === 'true') {
      const refreshData = async () => {
        try {
          await refreshUser();
          await fetchDashboardData();
          const newSearchParams = new URLSearchParams(searchParams);
          newSearchParams.delete('completed');
          newSearchParams.delete('refresh');
          setSearchParams(newSearchParams, { replace: true });
        } catch (error) {
          console.error('Error refreshing dashboard data:', error);
        }
      };
      refreshData();
    }
  }, [searchParams, setSearchParams, refreshUser, fetchDashboardData]);

  if (loading) {
    return (
      <DashboardContainer>
        <LoadingSpinner />
      </DashboardContainer>
    );
  }

  if (error) {
    return (
      <DashboardContainer>
        <div style={{ textAlign: 'center', padding: '5rem', color: colors.navy }}>
          <FaClipboardList style={{ fontSize: '4rem', color: '#ef4444', marginBottom: '1.5rem', opacity: 0.8 }} />
          <h3>Unable to load dashboard</h3>
          <p style={{ color: colors.textSub, marginBottom: '2rem' }}>{error}</p>
          <ActionButton to="#" onClick={() => window.location.reload()}>
            Retry Connection
          </ActionButton>
        </div>
      </DashboardContainer>
    );
  }

  const stats = dashboardData?.stats || {
    totalSurveys: 0,
    totalPointsEarned: 0,
    totalRedemptions: 0,
    currentPoints: user?.points || 0,
    approvedWithdrawalAmount: 0
  };

  const recentCompletedSurveys = dashboardData?.recentCompletedSurveys || [];
  const recentOpenSurveys = dashboardData?.recentOpenSurveys || [];

  return (
    <DashboardContainer>
      <WelcomeSection>
        <div className="content">
          <h1>Hello, {user?.firstName || 'Member'}! 👋</h1>
          <p>Ready to earn some rewards today?</p>
        </div>

        <div className="actions">
          <ActionButton to="/surveys">
            <FaPlay style={{ fontSize: '0.8rem' }} /> Start Earning Now
          </ActionButton>
        </div>
      </WelcomeSection>

      <StatsGrid>
        <StatCard accent={colors.sysGreen}>
          <StatIcon className="icon-bg" bg="#dcfce7" color={colors.sysGreen}>
            <FaClipboardList />
          </StatIcon>
          <StatContent>
            <h3>{stats.totalSurveys}</h3>
            <p>Surveys Done</p>
          </StatContent>
        </StatCard>

        <StatCard accent={colors.navy}>
          <StatIcon className="icon-bg" bg="#dbeafe" color={colors.navy}>
            <FaDollarSign />
          </StatIcon>
          <StatContent>
            <h3>{stats.currentPoints}</h3>
            <p>Active Points</p>
          </StatContent>
        </StatCard>

        <StatCard accent="#eab308">
          <StatIcon className="icon-bg" bg="#fef9c3" color="#eab308">
            <FaTrophy />
          </StatIcon>
          <StatContent>
            <h3>{stats.totalPointsEarned}</h3>
            <p>Lifetime Earned</p>
          </StatContent>
        </StatCard>

        <StatCard accent="#ef4444">
          <StatIcon className="icon-bg" bg="#fee2e2" color="#ef4444">
            <FaDollarSign />
          </StatIcon>
          <StatContent>
            <h3>₹{(stats.approvedWithdrawalAmount || 0).toLocaleString()}</h3>
            <p>Withdrawn</p>
          </StatContent>
        </StatCard>
      </StatsGrid>

      <ContentGrid>
        {/* Row 1, Col 1: Recommended For You */}
        <Card>
          <CardHeader>
            <h2>
              <div className="icon-wrapper"><FaStar color="#eab308" /></div>
              Recommended For You
            </h2>
            <ActionButton to="/surveys" style={{ fontSize: '0.75rem', padding: '0.4rem 0.8rem', background: '#f8fafc', color: colors.navy, boxShadow: 'none', border: '1px solid #e2e8f0' }}>View All <FaArrowRight /></ActionButton>
          </CardHeader>
          <CardContent>
            {recentOpenSurveys && recentOpenSurveys.length > 0 ? (
              recentOpenSurveys.map((survey, index) => (
                <SurveyItem
                  key={`open-${index}`}
                  $clickable={true}
                  $new={true}
                  onClick={() => handleSurveyClick(survey.id)}
                >
                  <SurveyInfo>
                    <h4>{survey.title}</h4>
                    <p>
                      <FaCalendarAlt style={{ marginRight: '0.4rem', color: colors.sysGreen }} />
                      {survey.estimatedTime} min • {survey.category || 'General'}
                    </p>
                  </SurveyInfo>
                  <PointsBadge bg="#dcfce7" color={colors.sysGreen}>
                    <FaBolt /> +{survey.pointsReward} pts
                  </PointsBadge>
                </SurveyItem>
              ))
            ) : (
              <EmptyState>
                <FaLeaf />
                <p>All caught up! Check back later for more earning opportunities.</p>
              </EmptyState>
            )}
          </CardContent>
        </Card>

        {/* Row 1, Col 2: Quick Actions */}
        <Card>
          <CardHeader>
            <h2>Quick Actions</h2>
          </CardHeader>
          <CardContent>
            <QuickActionItem to="/surveys">
              <div className="icon-box"><FaClipboardList /></div>
              <div className="text-box">
                <h4>Take Surveys</h4>
                <span>Earn points now</span>
              </div>
              <FaArrowRight className="arrow-icon" />
            </QuickActionItem>

            <QuickActionItem to="/rewards">
              <div className="icon-box" style={{ background: '#e0f2fe', color: colors.navy }}><FaGift /></div>
              <div className="text-box">
                <h4>Redeem Rewards</h4>
                <span>Exchange your points</span>
              </div>
              <FaArrowRight className="arrow-icon" />
            </QuickActionItem>

            <QuickActionItem to="/profile">
              <div className="icon-box" style={{ background: '#f3f4f6', color: colors.textMain }}><FaChartLine /></div>
              <div className="text-box">
                <h4>My Profile</h4>
                <span>Update your details</span>
              </div>
              <FaArrowRight className="arrow-icon" />
            </QuickActionItem>
          </CardContent>
        </Card>


        <Card>
          <CardHeader>
            <h2>
              <div className="icon-wrapper"><FaClipboardList /></div>
              Recent History
            </h2>
          </CardHeader>
          <CardContent>
            {recentCompletedSurveys && recentCompletedSurveys.length > 0 ? (
              recentCompletedSurveys.slice(0, 3).map((survey, index) => (
                <SurveyItem key={`completed-${index}`}>
                  <SurveyInfo>
                    <h4>{survey.Survey?.title || 'Untitled Survey'}</h4>
                    <p>
                      <FaCalendarAlt style={{ marginRight: '0.3rem' }} />
                      Completed on {(() => {
                        const date = new Date(survey.updatedAt || survey.createdAt);
                        return isNaN(date.getTime()) ? 'N/A' : date.toLocaleDateString();
                      })()}
                    </p>
                  </SurveyInfo>
                  <PointsBadge bg="#f1f5f9" color={colors.textSub}>+{survey.pointsEarned} pts</PointsBadge>
                </SurveyItem>
              ))
            ) : (
              <p style={{ textAlign: 'center', color: colors.textSub, fontStyle: 'italic', padding: '0.85rem', background: '#f8fafc', borderRadius: '10px', fontSize: '0.82rem' }}>No survey history yet</p>
            )}
          </CardContent>
        </Card>

        {/* Row 2, Col 2: Rewards Status */}
        <Card>
          <CardHeader>
            <h2><FaGift style={{ marginRight: '0.5rem', color: colors.sysGreen }} /> Rewards Status</h2>
          </CardHeader>
          <CardContent>
            <div style={{ textAlign: 'center', marginBottom: '0.75rem', padding: '0.85rem', background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)', borderRadius: '14px', position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', top: -20, right: -20, width: 60, height: 60, background: 'rgba(22, 163, 74, 0.05)', borderRadius: '50%' }}></div>
              <span style={{ display: 'block', fontSize: '0.7rem', color: colors.textSub, marginBottom: '0.25rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px' }}>Available Balance</span>
              <span style={{ fontSize: '2rem', fontWeight: '800', color: colors.navy, letterSpacing: '-2px', lineHeight: '1', display: 'block', marginBottom: '0.15rem' }}>{stats.currentPoints}</span>
              <span style={{ fontSize: '0.85rem', color: colors.textSub, fontWeight: '600' }}>points</span>
            </div>

            <div style={{ marginBottom: '0.6rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.3rem' }}>
                <span style={{ fontSize: '0.72rem', color: colors.textSub, fontWeight: '600' }}>Progress to ₹500</span>
                <span style={{ fontSize: '0.72rem', fontWeight: '800', color: colors.sysGreen }}>{animatedProgress.toFixed(0)}%</span>
              </div>
              <ProgressBar>
                <div className="fill" style={{ width: `${animatedProgress}%` }}></div>
              </ProgressBar>
            </div>

            <RedeemButton
              onClick={() => window.location.href = '/rewards'}
              disabled={stats.currentPoints < 100}
            >
              <FaGift />
              {stats.currentPoints >= 100 ? 'Redeem Now' : 'Earn More to Redeem'}
            </RedeemButton>
          </CardContent>
        </Card>
      </ContentGrid>
    </DashboardContainer>
  );
};

export default Dashboard;
